import { IState } from '@analysis/domain';

/**
 * Which analysts a report may list, given the organization's licence.
 *
 * Mirrors what the analysis screen shows the same user (`scopeAnalystsToOwn`):
 * without the review licence a user only ever sees their own analysis, and AI
 * analysts only appear when the organization is licensed for AI. The whole
 * report — results table, per-analyst counters and printable PDF — is derived
 * from the analyst list, so scoping the list scopes the report.
 */
export interface ReportAnalystScopeInput {
  /** `FeaturesService.canUse('reviewOthersAnalyses')`. */
  canReviewOthers: boolean;
  /** `FeaturesService.canUse('ai')`. */
  canUseAi: boolean;
  currentUserId?: string;
}

export interface ReportAnalystScope {
  /** `none` when own analyses are the only ones allowed but there is no user to scope to. */
  users: 'all' | 'own' | 'none';
  ai: boolean;
}

export function reportAnalystScope({
  canReviewOthers,
  canUseAi,
  currentUserId,
}: ReportAnalystScopeInput): ReportAnalystScope {
  const users = canReviewOthers ? 'all' : currentUserId ? 'own' : 'none';
  return { users, ai: canUseAi };
}

/** True when the scope excludes every analyst, so no read is worth issuing. */
export function isEmptyAnalystScope(scope: ReportAnalystScope): boolean {
  return scope.users === 'none' && !scope.ai;
}

/**
 * States that mean the analyst actually produced something to review. A `read`
 * state only says the sample was opened: listing such an analyst adds a
 * selector entry whose results table is all zeros, for every role — admins
 * included. Mirrors the filter the analysis screen applies in
 * `fetchUserAnalysts`.
 */
export const ANALYSED_STATES: IState[] = [IState.INPROGRES, IState.ANALYZED];
